import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import { Box } from "@mui/material";

import { ActionButton, CardLayout, InputField, SelectField } from "../v3";
import { get_date, RadioBtnCollection } from "../generalComponets";
import {
  AccountService,
  AdminService,
  FinanceService,
  HumanResourceService,
  ProjectService,
  PurchaseService,
} from "../../services";
import {
  ADD_WORKER_PAYMENT,
  ADD_VENDOR_PAYMENT,
  CREATE_SUBCONTRACTOR_TRANSACTION,
  ADD_EMPLOYEE_SALARY,
  FINANCE_CATEGORY_IDS,
  DestinationEnum,
  ENABLE_WORKER_PAYMENT_BACKDATE_ENTRY,
  ENABLE_EMPLOYEE_SALARY_BACKDATE_ENTRY,
  ENABLE_VENDOR_PAYMENT_BACKDATE_ENTRY,
  ENABLE_SUBCONTRACTOR_TRANSACTION_BACKDATE_ENTRY,
  ENABLE_USER_PAYMENT_BACKDATE_ENTRY,
  ENABLE_ACCOUNT_PAYMENT_BACKDATE_ENTRY,
  VIEW_ACCOUNT,
  ENABLE_WORKER_ADVANCE_PAYMENT,
  ENABLE_VENDOR_ADVANCE_PAYMENT,
  ENABLE_SUBCONTRACTOR_ADVANCE_PAYMENT,
  ADD_USER_PAYMENT,
  ADD_ACCOUNT_TRANSACTION,
} from "../../constants";
import { addTransactionValidationRules, validateAdvancePayment } from "../../pages/transaction/validation";
import { TransactionTypes, useHasPermission } from "../../utils";
import { markTableReset } from "../../utils/tableStateStorage";

export const AddTransactionForm = () => {
  const [destinationType, setDestinationType] = useState("");
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedProjectStage, setSelectedProjectStage] = useState(null);
  const [stageIds, setStageIds] = useState([]);
  const [projectStages, setProjectStages] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState({
    destinationId: undefined,
    amount: undefined,
    type: undefined,
    status: "COMPLETED",
    description: undefined,
    date: get_date(Date.now(), "YYYY-MM-DD"),
    projectId: undefined,
    projectStageId: undefined,
    fromAccountId: undefined,
    toAccountId: undefined,
  });
  const [errors, setErrors] = useState({});
  const [destinationBalance, setDestinationBalance] = useState(null);
  const [loading, setLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const workerId = searchParams.get("workerId");
  const employeeId = searchParams.get("employeeId");
  const vendorId = searchParams.get("vendorId");
  const subcontractorId = searchParams.get("subcontractorId");
  const userId = searchParams.get("userId");
  const accountId = searchParams.get("accountId");
  const projectId = searchParams.get("projectId");
  const selectionRequestRef = useRef(0);

  const navigate = useNavigate();
  const { user, permissions } = useSelector((state) => state.user);
  const { ownerUserId: companyAdminId } = user.company;

  const backdatePermissionsMap = {
    WORKER: ENABLE_WORKER_PAYMENT_BACKDATE_ENTRY,
    EMPLOYEE: ENABLE_EMPLOYEE_SALARY_BACKDATE_ENTRY,
    VENDOR: ENABLE_VENDOR_PAYMENT_BACKDATE_ENTRY,
    SUBCONTRACTOR: ENABLE_SUBCONTRACTOR_TRANSACTION_BACKDATE_ENTRY,
    USER: ENABLE_USER_PAYMENT_BACKDATE_ENTRY,
    ACCOUNT: ENABLE_ACCOUNT_PAYMENT_BACKDATE_ENTRY,
  };
  const currentBackdatePermission = backdatePermissionsMap[destinationType];
  const enableBackDate = useHasPermission(currentBackdatePermission);
  const hasViewAccount = useHasPermission(VIEW_ACCOUNT);

  const advancePaymentPermissionMap = {
    WORKER: ENABLE_WORKER_ADVANCE_PAYMENT,
    VENDOR: ENABLE_VENDOR_ADVANCE_PAYMENT,
    SUBCONTRACTOR: ENABLE_SUBCONTRACTOR_ADVANCE_PAYMENT,
  };
  const advancePermission = advancePaymentPermissionMap[destinationType];
  const enableAdvancePayment = useHasPermission(advancePermission);

  // setting destination type and destination id
  useEffect(() => {
    if (workerId) {
      setDestinationType("WORKER");
      setPaymentDetails((prev) => ({
        ...prev,
        destinationId: workerId,
      }));
      fetchData(workerId, "WORKER");
    } else if (employeeId) {
      setDestinationType("EMPLOYEE");
      setPaymentDetails((prev) => ({
        ...prev,
        destinationId: employeeId,
      }));
      fetchData(employeeId, "EMPLOYEE");
    } else if (vendorId) {
      setDestinationType("VENDOR");
      setPaymentDetails((prev) => ({
        ...prev,
        destinationId: vendorId,
      }));
      fetchData(vendorId, "VENDOR");
    } else if (subcontractorId) {
      setDestinationType("SUBCONTRACTOR");
      setPaymentDetails((prev) => ({
        ...prev,
        destinationId: subcontractorId,
      }));
      fetchData(subcontractorId, "SUBCONTRACTOR");
    } else if (userId) {
      setDestinationType("USER");
      setPaymentDetails((prev) => ({
        ...prev,
        destinationId: userId,
      }));
      fetchData(userId, "USER");
    } else if (accountId) {
      setDestinationType("ACCOUNT");
      setPaymentDetails((prev) => ({
        ...prev,
        destinationId: accountId,
      }));
      fetchData(accountId, "ACCOUNT");
    } else if (projectId) {
      setDestinationType("SUBCONTRACTOR");
      fetchProject(projectId);
      fetchProjectStages(projectId);
    }
  }, [workerId, employeeId, vendorId, subcontractorId, userId, accountId, projectId]);

  // reset selected destination when type changes
  useEffect(() => {
    setSelectedDestination(null);
    setDestinationBalance(null);
    setPaymentDetails((prev) => ({
      ...prev,
      destinationId: undefined,
      amount: undefined,
    }));
  }, [destinationType]);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const stageFilter = useMemo(
    () => ({
      ids: stageIds,
    }),
    [stageIds],
  );

  const fetchData = async (destinationId, type) => {
    try {
      const apiFn = getDestinationData(type);
      const { data: response } = await apiFn(destinationId);
      setSelectedDestination(response);
      setPaymentDetails((prev) => ({
        ...prev,
        destinationId: response?.id,
        amount: getAutoFillAmount(response, type),
      }));
      if (["WORKER", "VENDOR", "SUBCONTRACTOR"].includes(type)) {
        setDestinationBalance(invertBalance(response?.account?.balance));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchProject = async () => {
    try {
      const { data: project } = await ProjectService.getProjectById(projectId);
      handleProjectChange(project);
    } catch (error) {
      console.error("🚀 ~ fetchProject ~ error:", error);
    }
  };

  const fetchProjectStages = async (projectId) => {
    try {
      const { data: first } = await ProjectService.getProjectStages({
        projectId,
        page: 1,
        limit: 10,
      });

      const { data } = await ProjectService.getProjectStages({
        projectId,
        page: 1,
        limit: first.count,
      });

      const stages = [...new Set(data.rows.map((item) => item.stageId))];

      setStageIds(stages.join(","));
      setProjectStages(data.rows);
    } catch (error) {
      console.error("🚀 ~ fetchProjectStages ~ error:", error);
    }
  };

  const fetchAccounts = async () => {
    try {
      const { data: accounts } = await AccountService.getAdminAndUserAccounts({
        hasViewAccount,
        user,
        companyAdminId,
      });
      setSelectedAccount(accounts.rows[0]);
      setPaymentDetails((prev) => ({
        ...prev,
        fromAccountId: accounts.rows[0]?.id,
      }));
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // get single destination API
  const getDestinationData = (type) => {
    switch (type) {
      case "WORKER":
      case "EMPLOYEE":
        return HumanResourceService.getEmployeeById;
      case "VENDOR":
      case "SUBCONTRACTOR":
        return PurchaseService.getVendorById;
      case "USER":
        return AdminService.getUserDetails;
      case "ACCOUNT":
        return AccountService.getAccountById;
      default:
        return () => Promise.resolve({ data: {} });
    }
  };

  const handleDestinationTypeChange = (item) => {
    selectionRequestRef.current++;
    setErrors({});
    setDestinationType(item.value);
  };

  const getDestinationOptions = useCallback((type) => {
    switch (type) {
      case "WORKER":
      case "EMPLOYEE":
        return HumanResourceService.getEmployees;
      case "VENDOR":
      case "SUBCONTRACTOR":
        return PurchaseService.getVendors;
      case "USER":
        return AdminService.getUserList;
      case "ACCOUNT":
        return AccountService.getAdminAndUserAccounts;
      default:
        return () => Promise.resolve({ data: { rows: [] } });
    }
  }, []);

  const getOptionFilters = (type, userId) => {
    switch (type) {
      case "WORKER":
        return { active: true, type: "WORKER", includeBalance: true };
      case "EMPLOYEE":
        return { active: true, type: "EMPLOYEE" };
      case "VENDOR":
        return { type: "VENDOR", active: true, includeBalance: true };
      case "SUBCONTRACTOR":
        return { type: "SUBCONTRACTOR", active: true, includeBalance: true };
      case "USER":
        return { type: "USER", active: true };
      case "ACCOUNT":
        return { hasViewAccount, user, companyAdminId };
      default:
        return {};
    }
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    setPaymentDetails((prev) => ({ ...prev, [name]: value }));

    if (name === "amount") {
      let amountError;

      if (value !== "" && Number(value) < 1) {
        amountError = "Amount must be greater than 0";
      } else {
        amountError = validateAdvancePayment({
          amount: value,
          destinationType,
          destinationBalance,
          enableAdvancePayment,
        });
      }

      setErrors((prev) => {
        const nextErrors = { ...prev };

        if (amountError) nextErrors.amount = amountError;
        else delete nextErrors.amount;

        return nextErrors;
      });
    }
  };

  const handleDestinationChange = async (selected) => {
    const requestId = ++selectionRequestRef.current;
    setErrors({});
    setSelectedDestination(selected);
    setPaymentDetails((prev) => ({
      ...prev,
      destinationId: selected?.id ?? null,
      amount: undefined,
    }));
    if (!selected) {
      setDestinationBalance(null);
      return;
    }
    let destination = selected;

    if (destinationType === "USER" && selected?.id) {
      try {
        const { data } = await AdminService.getUserDetails(selected.id);
        destination = data;
      } catch (error) {
        console.error("Failed to fetch user balance:", error);
      }
    }
    if (requestId !== selectionRequestRef.current) return;

    const amount = getAutoFillAmount(destination, destinationType);

    setDestinationBalance(
      ["WORKER", "VENDOR", "SUBCONTRACTOR"].includes(destinationType) ? invertBalance(destination?.account?.balance) : null,
    );

    setPaymentDetails((prev) => ({
      ...prev,
      destinationId: selected?.id ?? null,
      amount,
    }));
  };

  const handleProjectChange = (selected) => {
    setStageIds([]);
    setSelectedProjectStage(null);
    setSelectedProject(selected);
    setPaymentDetails((prev) => ({ ...prev, projectId: selected?.id, projectStageId: null }));
    if (selected?.id) fetchProjectStages(selected.id);
  };

  const handleProjectStageChange = (selected) => {
    const projectStage = projectStages.find((item) => item?.stageId === selected?.id);
    setSelectedProjectStage(selected);
    setPaymentDetails((prev) => ({ ...prev, projectStageId: projectStage?.id || null }));
  };

  const handleAccountChange = (selected) => {
    setSelectedAccount(selected);
    setPaymentDetails((prev) => ({ ...prev, fromAccountId: selected?.id || null }));
  };

  const handleCancelClick = () => {
    handleClearForm();
    navigate(-1);
  };

  const handleClearForm = () => {
    setErrors({});
    setDestinationBalance(null);
    setPaymentDetails({
      destinationId: undefined,
      amount: undefined,
      type: undefined,
      status: "COMPLETED",
      description: undefined,
      date: get_date(Date.now(), "YYYY-MM-DD"),
      projectId: undefined,
      projectStageId: undefined,
      fromAccountId: undefined,
      toAccountId: undefined,
    });
    setSelectedDestination(null);
    setSelectedProject(null);
    setSelectedProjectStage(null);
    setSelectedAccount(null);
  };

  const handleSavePayment = async () => {
    if (loading) return;
    setErrors({});

    if (!destinationType) {
      setErrors({ destinationId: `Please select a payment type` });
      return;
    }
    const validationErrors = addTransactionValidationRules(paymentDetails, destinationType, {
      destinationBalance,
      enableAdvancePayment,
    });
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);

      // add accountId for USER/ACCOUNT destination types
      if (destinationType === "USER") {
        const { data: userAccount } = await AccountService.getAccounts({ ownerUserId: selectedDestination?.id });
        selectedDestination.accountId = userAccount.rows[0]?.id;
      }
      if (destinationType === "ACCOUNT") selectedDestination.accountId = selectedDestination.id;

      const transactionPayload = {
        type: TransactionTypes[destinationType],
        status: paymentDetails.status,
        description: paymentDetails.description,
        projectId: paymentDetails.projectId,
        projectStageId: paymentDetails.projectStageId,
        fromAccountId: paymentDetails.fromAccountId,
        toAccountId: Number(selectedDestination?.accountId),
        amount: Number(paymentDetails.amount),
        date: moment(`${paymentDetails.date} ${moment().format("HH:mm:ss")}`).toISOString(),
        ...((destinationType === "ACCOUNT" || destinationType === "USER") && {
          isAccountTransaction: true,
        }),
      };

      const expensePayload = {
        amount: Number(paymentDetails.amount),
        date: moment(`${paymentDetails.date} ${moment().format("HH:mm:ss")}`).toISOString(),
        categoryId: FINANCE_CATEGORY_IDS.SUBCONTRACTOR_PAYMENT,
        status: paymentDetails.status,
        description: paymentDetails.description,
        projectId: paymentDetails.projectId,
        projectStageId: paymentDetails.projectStageId,
        destinationType: DestinationEnum.vendors,
        destinationId: Number(selectedDestination?.id),
        isPaid: true,
        paidAmount: Number(paymentDetails.amount),
        fromAccountId: paymentDetails.fromAccountId,
      };

      if (destinationType === "SUBCONTRACTOR") await FinanceService.createExpense(expensePayload);
      else await FinanceService.addTransaction(transactionPayload);

      toast.success("Payment added successfully");
      handleClearForm();
      if (workerId || employeeId || vendorId || subcontractorId || userId || accountId) {
        // A new record would be hidden by the filters the list had, so it opens fresh.
        markTableReset();
        navigate(-1);
      } else if (projectId) navigate(`/projects/${projectId}?tab=contracts`);
    } catch (error) {
      let errorMessage = `Error adding payment`;
      const response = error?.response?.data;
      if (Array.isArray(response?.message)) {
        errorMessage = response.message.join(", ");
      } else if (typeof response?.message === "string") {
        errorMessage = response.message;
      }

      toast.error(errorMessage);
      console.error(`🚀 ~ handleSavePayment ~ error:`, error);
    } finally {
      setLoading(false);
    }
  };

  const invertBalance = (balance) => -(Number(balance) || 0);
  const getAutoFillAmount = (selected, type) => {
    if (!selected) return undefined;

    const balance =
      type === "USER" ? selected?.accounts?.[0]?.balance : type === "ACCOUNT" ? selected?.balance : selected?.account?.balance;

    if (balance == null) return undefined;

    const amount = Number(balance);

    if (!Number.isFinite(amount)) return undefined;

    return amount < 0 ? -amount : undefined;
  };
  const getAmountLabel = ({ enableAdvancePayment, destinationType, destinationBalance }) => {
    if (!enableAdvancePayment && ["WORKER", "VENDOR", "SUBCONTRACTOR"].includes(destinationType) && destinationBalance !== null) {
      return `Amount (Balance: ${Number(destinationBalance).toLocaleString()})`;
    }

    return "Amount";
  };

  const destinationTypeOptions = [
    { value: "WORKER", title: "Worker", permission: ADD_WORKER_PAYMENT },
    { value: "EMPLOYEE", title: "Employee", permission: ADD_EMPLOYEE_SALARY },
    { value: "VENDOR", title: "Vendor", permission: ADD_VENDOR_PAYMENT },
    { value: "SUBCONTRACTOR", title: "Subcontractor", permission: CREATE_SUBCONTRACTOR_TRANSACTION },
    { value: "USER", title: "User", permission: ADD_USER_PAYMENT }, // TODO: TBD
    { value: "ACCOUNT", title: "Account", permission: ADD_ACCOUNT_TRANSACTION },
  ].filter((option) => permissions.includes(option.permission));

  const isDestinationPreselected = Boolean(workerId || employeeId || vendorId || subcontractorId || userId || accountId);
  const shouldShowRadioBtn = !isDestinationPreselected && !projectId;

  return (
    <CardLayout>
      {/* hide payment type selection if destination is preselected */}
      {shouldShowRadioBtn && (
        <RadioBtnCollection
          title={"Payment Type"}
          list={destinationTypeOptions}
          onSelect={handleDestinationTypeChange}
          style={{
            alignItems: "center",
            marginTop: -8,
            marginBottom: 16,
          }}
          value={destinationType}
        />
      )}
      <Box display="flex" gap={2}>
        <SelectField
          label={destinationType}
          value={selectedDestination}
          getData={getDestinationOptions(destinationType)}
          getDataStaticFilters={getOptionFilters(destinationType, user.id)}
          optionValueFields={destinationType === "USER" ? ["firstName", "lastName"] : ["name"]}
          disabled={isDestinationPreselected}
          required
          error={!!errors.destinationId}
          helperText={errors.destinationId}
          onChange={handleDestinationChange}
          style={{ width: "100%" }}
        />
        <SelectField
          label="From Account"
          value={selectedAccount}
          getData={AccountService.getAdminAndUserAccounts}
          getDataStaticFilters={{ hasViewAccount, user, companyAdminId }}
          required
          error={!!errors.fromAccountId}
          helperText={errors.fromAccountId}
          onChange={handleAccountChange}
          style={{ width: "100%" }}
        />
        <InputField
          required
          label={getAmountLabel({
            enableAdvancePayment,
            destinationType,
            destinationBalance,
          })}
          name="amount"
          type="number"
          value={paymentDetails.amount}
          onChange={updateDetailsState}
          error={!!errors.amount}
          helperText={errors.amount}
          fullWidth
        />
      </Box>

      <Box display="flex" gap={2}>
        <InputField
          required
          label="Date"
          name="date"
          type="date"
          value={paymentDetails.date}
          onChange={updateDetailsState}
          error={!!errors.date}
          helperText={errors.date}
          fullWidth
          inputSlotProps={{
            htmlInput: {
              min: enableBackDate ? undefined : get_date(Date.now(), "YYYY-MM-DD"),
              onKeyDown: enableBackDate ? undefined : (e) => e.preventDefault(),
            },
          }}
        />
        <SelectField
          label="Project"
          value={selectedProject}
          getData={ProjectService.getProjectList}
          getDataStaticFilters={{ status: ["TODO", "INPROGRESS"] }}
          disabled={!!projectId}
          required={destinationType === "SUBCONTRACTOR"}
          error={!!errors.projectId}
          helperText={errors.projectId}
          onChange={handleProjectChange}
          style={{ width: "100%" }}
        />
        <SelectField
          label="Stage"
          value={selectedProjectStage}
          getData={ProjectService.getStages}
          getDataStaticFilters={stageFilter}
          disabled={stageIds.length === 0}
          error={!!errors.projectStageId}
          helperText={errors.projectStageId}
          onChange={handleProjectStageChange}
          style={{ width: "100%" }}
        />
      </Box>
      <Box display="flex" gap={2}>
        <InputField
          label="Description"
          name="description"
          value={paymentDetails.description}
          onChange={updateDetailsState}
          error={!!errors.description}
          helperText={errors.description}
          fullWidth
          autoComplete="on"
        />
      </Box>
      <Box display="flex" justifyContent="flex-end" gap={2}>
        <ActionButton buttonText="cancel" variant="outlined" onButtonClick={handleCancelClick} />
        <ActionButton buttonText="save" onButtonClick={handleSavePayment} loading={loading} />
      </Box>
    </CardLayout>
  );
};
